/// <reference types="@angular/localize" />

import { APP_CONFIG } from "./app/injection-token/app-config";
import { AppComponent } from './app/app.component';
import { provideAnimations } from '@angular/platform-browser/animations';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { CustomDateParserFormatter } from './app/datepicker/CustomDateParserFormatter';
import { NgbDateParserFormatter, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { provideRouter, Router, withComponentInputBinding } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { TraceService } from '@sentry/angular';
import { ExceptionHandler } from './app/exceptionHandler/exception-handler';
import { CacheInterceptor } from './app/interceptors/cache-interceptor';
import { AuthInterceptor } from './app/interceptors/auth-interceptor';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import {
  ErrorHandler,
  importProvidersFrom,
  inject,
  Injector,
  LOCALE_ID,
  NgZone,
  provideAppInitializer,
  provideExperimentalCheckNoChangesForDebug,
  provideExperimentalZonelessChangeDetection
} from '@angular/core';
import { AppConfig } from "./app/model/AppConfig";
import { ROUTES } from "./routes";
import { UserService } from "./app/service/user.service";
import { config as rxjsConfig, firstValueFrom } from "rxjs";
import { ErrorInterceptor } from "./app/interceptors/error-interceptor";
import { UserDetails } from "./app/model/user/userDetails";

fetch('/api/frontendConfiguration').then(response => response.json()).then((config: AppConfig) => {
  if (config.sentry.dsn) {
    Sentry.init({
      dsn: config.sentry.dsn,
      integrations: [
        // Registers and configures the Tracing integration,
        // which automatically instruments your application to monitor its
        // performance, including custom Angular routing instrumentation
        Sentry.browserTracingIntegration(),
        // Registers the Replay integration,
        // which automatically captures Session Replays
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),

        Sentry.httpClientIntegration(),
      ],

      environment: config.environment,

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: config.sentry.tracesSampleRate,

      // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [config.serviceUrl, /^\/api\//],

      // Capture Replay for 10% of all sessions,
      // plus for 100% of sessions with an error
      replaysSessionSampleRate: config.sentry.replaysSessionSampleRate,
      replaysOnErrorSampleRate: config.sentry.replaysOnErrorSampleRate,
    });
    rxjsConfig.onUnhandledError = (err: any) => Sentry.captureMessage('RxJS Unhandled Error', { extra: err });
  }
  bootstrapApplication(AppComponent, {
    providers: [
      provideRouter(ROUTES, withComponentInputBinding()),
      {
        provide: APP_CONFIG,
        useValue: config
      },
      importProvidersFrom(
        BrowserModule,
        NgbModule,
      ),
      {
        provide: LOCALE_ID,
        useFactory: () => $localize.locale ?? 'pl'
      },
      {
        provide: APP_BASE_HREF,
        useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(),
        deps: [PlatformLocation]
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: AuthInterceptor,
        multi: true
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: CacheInterceptor,
        multi: true
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ErrorInterceptor,
        multi: true,
        deps: [ErrorHandler]
      },
      {
        provide: ErrorHandler,
        useClass: ExceptionHandler,
        deps: [NgZone, Injector]
      },
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
      provideAppInitializer(() => {
        inject(TraceService);
      }),
      provideAppInitializer(() => {
        const initializerFn = (initUser)(inject(UserService));
        return initializerFn();
      }),
      provideAppInitializer(() => {
        const initializerFn = (getCsrfToken)(inject(HttpClient));
        return initializerFn();
      }),
      {
        provide: NgbDateParserFormatter,
        useClass: CustomDateParserFormatter
      },
      provideHttpClient(withInterceptorsFromDi()),
      provideAnimations(),
      provideExperimentalZonelessChangeDetection()
    ]
  }).catch(err => console.error(err));
}).catch(err => console.error(err));

function initUser(userService: UserService) {
  return (): Promise<UserDetails> => firstValueFrom(userService.init$());
}

function getCsrfToken(http: HttpClient) {
  return (): Promise<object> => firstValueFrom(http.get('/api/csrf'));
}
